import { Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, ReactNode, useCallback } from 'react';
import styles from './SegmentedItem.module.scss';
import { useGlobalContainerRef } from '@/wc/helper/ref';

const cx = classnames.bind(styles);

export type Props = {
  /** Icon-only item, so this is the item's accessible name — not decoration. */
  label: string;
  /** Text shown in the hover tooltip (NA-5596). */
  tooltip: string;
  icon: ReactNode;
  selected: boolean;
  onSelect: () => void;
  testId?: string;
};

/**
 * One option inside a segmented control.
 *
 * Rendered as `role='radio'` rather than a plain button: the options are
 * mutually exclusive, so a radio group is what conveys "one of these is
 * active" to assistive tech. Roving `tabIndex` keeps the whole group a single
 * tab stop — arrow keys move within it, handled by the parent.
 */
export const SegmentedItem: FC<Props> = memo(
  ({ label, tooltip, icon, selected, onSelect, testId }) => {
    const handleClick = useCallback(() => {
      if (!selected) onSelect();
    }, [selected, onSelect]);
    const container = useGlobalContainerRef();

    return (
      // Without `getPopupContainer`, the shared `Tooltip` falls back to the
      // trigger's own `parentNode` (not `document.body`), so the popup can
      // be painted over by sibling row content below it (NA-5596).
      <Tooltip title={tooltip} getPopupContainer={container}>
        <button
          type='button'
          role='radio'
          aria-checked={selected}
          aria-label={label}
          tabIndex={selected ? 0 : -1}
          className={cx('item', { selected })}
          onClick={handleClick}
          data-testid={testId}
        >
          <span className={cx('icon')}>{icon}</span>
        </button>
      </Tooltip>
    );
  }
);
SegmentedItem.displayName = 'SegmentedItem';
